import { foldText } from '@secretario/shared';

/**
 * Tudo o que dá para ajustar, num índice só, para a busca dos Ajustes. Cada linha diz onde
 * mora (o grupo da regra 13 do AGENTS.md) e para onde o toque leva. Opção nova nos Ajustes
 * entra aqui também, com as palavras que a pessoa usaria para procurar.
 */
export type SettingEntry = {
  title: string;
  hint: string;
  group: string;
  /** Página onde o ajuste mora. `/ajustes#grupo` = na própria página dos Ajustes. */
  href: string;
  /** Palavras que a pessoa usaria (sinônimos, nomes antigos, termos do dia a dia). */
  keywords?: string;
};

export const SETTINGS_INDEX: SettingEntry[] = [
  // Você
  {
    title: 'Seu perfil',
    hint: 'Nome, apelido, foto e cidade',
    group: 'Você',
    href: '/perfil',
    keywords: 'nome foto avatar cidade apelido aniversario telefone numero',
  },
  {
    title: 'Disponível ou ocupado',
    hint: 'O Secretário segura o que der quando você está ocupado',
    group: 'Você',
    href: '/ajustes#voce',
    keywords: 'status ocupado disponivel nao perturbe',
  },
  {
    title: 'Segurança',
    hint: 'Passkey, autenticador, e-mail de recuperação e Google',
    group: 'Você',
    href: '/ajustes/seguranca',
    keywords:
      'senha passkey digital autenticador google authenticator codigo 2fa email recuperacao entrar login',
  },
  {
    title: 'Ligar o Google',
    hint: 'Entrar com a sua conta Google, sem SMS',
    group: 'Você',
    href: '/ajustes/seguranca',
    keywords: 'google gmail login entrar',
  },
  {
    title: 'Convidar amigos',
    hint: 'Mandar o seu link de convite',
    group: 'Você',
    href: '/ajustes#voce',
    keywords: 'convite indicar compartilhar link amigo',
  },
  // Seu Secretário
  {
    title: 'Mudar o jeito',
    hint: 'Rosto, voz e nome do Secretário',
    group: 'Seu Secretário',
    href: '/mudar-jeito',
    keywords: 'personagem rosto voz nome aparencia roupa caio clara igor gota',
  },
  {
    title: 'Jeito de falar e de chamar',
    hint: 'Respostas em partes, aprovação em conversa, assinatura',
    group: 'Seu Secretário',
    href: '/ajustes/secretario',
    keywords: 'tom fala chamar resposta partes',
  },
  {
    title: 'Deixar na mesa',
    hint: 'Chamar pelo nome com o app aberto',
    group: 'Seu Secretário',
    href: '/ajustes/secretario',
    keywords: 'palavra de ativacao chamar pelo nome mesa ô microfone',
  },
  {
    title: 'Assistente do celular',
    hint: 'Usar o Secretário como assistente do Android',
    group: 'Seu Secretário',
    href: '/ajustes/secretario',
    keywords: 'assistente padrao android botao',
  },
  {
    title: 'Não respondo em grupos',
    hint: 'Grupos ficam de fora das respostas automáticas',
    group: 'Seu Secretário',
    href: '/ajustes/secretario',
    keywords: 'grupo whatsapp resposta automatica',
  },
  {
    title: 'Tela inicial',
    hint: 'O que aparece na tela inicial',
    group: 'Seu Secretário',
    href: '/ajustes/inicio',
    keywords: 'atalhos inicio home tela inicial botoes',
  },
  {
    title: 'Trilha de aprendizagem',
    hint: 'Os primeiros passos com o Secretário',
    group: 'Seu Secretário',
    href: '/ajustes#secretario',
    keywords: 'tutorial tour aprender passos',
  },
  // Notificações
  {
    title: 'Mensagens novas',
    hint: 'Notificação quando alguém te escreve',
    group: 'Notificações',
    href: '/ajustes#notificacoes',
    keywords: 'mensagem notificacao conversa',
  },
  {
    title: 'Lembretes e agenda',
    hint: 'Na hora e antes dos compromissos',
    group: 'Notificações',
    href: '/ajustes#notificacoes',
    keywords: 'lembrete agenda compromisso aviso',
  },
  {
    title: 'Notificações do Secretário',
    hint: 'Quando ele precisar de uma decisão sua',
    group: 'Notificações',
    href: '/ajustes#notificacoes',
    keywords: 'secretario decisao aprovacao aviso',
  },
  {
    title: 'Notificações',
    hint: 'Mensagens, lembretes e avisos do Secretário',
    group: 'Notificações',
    href: '/ajustes/avisos',
    keywords: 'avisos notificacao push alerta som',
  },
  {
    title: 'Mostrar o texto na notificação',
    hint: 'Ver a mensagem e responder pela notificação',
    group: 'Notificações',
    href: '/ajustes/avisos',
    keywords: 'previa texto notificacao responder',
  },
  {
    title: 'Lembretes garantidos',
    hint: 'Alarme do aparelho para os lembretes',
    group: 'Notificações',
    href: '/ajustes/avisos',
    keywords: 'alarme lembrete hora despertador',
  },
  {
    title: 'Ler mensagens de outros apps',
    hint: 'WhatsApp e outros, só no Android',
    group: 'Notificações',
    href: '/ajustes/avisos',
    keywords: 'whatsapp outros apps notificacoes leitura',
  },
  {
    title: 'Sugerir respostas',
    hint: 'Rascunho de resposta para mensagens de outros apps',
    group: 'Notificações',
    href: '/ajustes/avisos',
    keywords: 'rascunho resposta sugestao',
  },
  // Conexões
  {
    title: 'Sua IA',
    hint: 'Usar a sua própria chave de IA (plano Completo)',
    group: 'Conexões',
    href: '/ajustes/sua-ia',
    keywords: 'chave api gemini openai modelo inteligencia artificial byok',
  },
  {
    title: 'Telas conectadas',
    hint: 'TV, notebook e apresentações',
    group: 'Conexões',
    href: '/ajustes/telas',
    keywords: 'tv tela grande notebook smart view transmitir apresentacao',
  },
  {
    title: 'Sites que abrem na tela',
    hint: 'Links que o Secretário pode abrir na TV',
    group: 'Conexões',
    href: '/ajustes/telas',
    keywords: 'site link endereco pagina liberar abrir tv navegador',
  },
  {
    title: 'Aparelhos com a sua conta',
    hint: 'Onde você está entrando',
    group: 'Conexões',
    href: '/aparelhos',
    keywords: 'aparelhos dispositivos sessoes celular sair',
  },
  // Privacidade
  {
    title: 'Usar inteligência artificial',
    hint: 'A permissão para o Secretário usar IA',
    group: 'Privacidade',
    href: '/ajustes#privacidade',
    keywords: 'ia inteligencia artificial consentimento',
  },
  {
    title: 'O que eu sei de você',
    hint: 'A memória do Secretário, item a item',
    group: 'Privacidade',
    href: '/sobre-voce',
    keywords: 'memoria sobre voce dados lembrar esquecer',
  },
  {
    title: 'Aparecer pra quem tem meu número',
    hint: 'Quem te salvou nos contatos vê que você está aqui',
    group: 'Privacidade',
    href: '/ajustes#privacidade',
    keywords: 'descoberta encontrar numero contatos visivel',
  },
  {
    title: 'Confirmação de leitura',
    hint: 'Mostrar quando você leu',
    group: 'Privacidade',
    href: '/ajustes/privacidade',
    keywords: 'lido visto leitura tique azul',
  },
  {
    title: 'Mostrar quando estou escrevendo',
    hint: 'O "digitando…" na conversa',
    group: 'Privacidade',
    href: '/ajustes/privacidade',
    keywords: 'digitando escrevendo',
  },
  {
    title: 'Mostrar quando estou online',
    hint: 'Seu status para os contatos',
    group: 'Privacidade',
    href: '/ajustes/privacidade',
    keywords: 'online visto por ultimo status',
  },
  {
    title: 'Pesquisar na internet',
    hint: 'O Secretário pode pesquisar para responder',
    group: 'Privacidade',
    href: '/ajustes/privacidade',
    keywords: 'internet pesquisa busca web',
  },
  {
    title: 'Proteção de ponta a ponta',
    hint: 'A chave das suas conversas',
    group: 'Privacidade',
    href: '/ajustes/privacidade',
    keywords: 'criptografia chave recuperacao ponta a ponta',
  },
  // Plano e uso
  {
    title: 'Plano e uso',
    hint: 'Assinatura, créditos e quanto você já usou',
    group: 'Plano e uso',
    href: '/plano',
    keywords: 'plano assinatura pagar creditos cancelar completo pessoal preco uso limite',
  },
  // Ajuda e conta
  {
    title: 'Ajuda e sobre',
    hint: 'Dúvidas comuns e como falar com a gente',
    group: 'Ajuda e conta',
    href: '/ajuda',
    keywords: 'ajuda suporte duvida contato falar',
  },
  {
    title: 'Privacidade e termos',
    hint: 'O que eu guardo, para quê e como apagar',
    group: 'Ajuda e conta',
    href: '/privacidade',
    keywords: 'termos politica lgpd dados',
  },
  {
    title: 'Sair desta conta',
    hint: 'Sair neste aparelho',
    group: 'Ajuda e conta',
    href: '/ajustes#conta',
    keywords: 'sair logout deslogar',
  },
  {
    title: 'Apagar minha conta',
    hint: 'Apagar a conta e os dados',
    group: 'Ajuda e conta',
    href: '/ajustes/apagar',
    keywords: 'excluir apagar deletar conta',
  },
];

/** Busca sem acento e sem caixa; todas as palavras precisam aparecer. */
export function searchSettings(
  query: string,
  entries: SettingEntry[] = SETTINGS_INDEX,
): SettingEntry[] {
  const words = foldText(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return entries
    .map((entry) => {
      const title = foldText(entry.title);
      const all = foldText(`${entry.title} ${entry.hint} ${entry.group} ${entry.keywords ?? ''}`);
      if (!words.every((word) => all.includes(word))) return null;
      // No título pesa mais (aparece primeiro).
      const score = words.filter((word) => title.includes(word)).length;
      return { entry, score };
    })
    .filter((item): item is { entry: SettingEntry; score: number } => item !== null)
    .sort((a, b) => b.score - a.score || a.entry.title.length - b.entry.title.length)
    .map((item) => item.entry);
}
